import { Fragment } from 'react';
import {
  DropdownMenu,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuPortal,
  DropdownMenuContent,
} from '@antimatterhq/ui/components/dropdownMenu';
import { Button } from '@antimatterhq/ui/components/button';

type SubMenuItem = {
  label?: string;
  icon?: React.ReactNode;
  onClick?: () => void;
  customContent?: () => React.ReactNode;
};

type ExclusiveSubMenuItem =
  | { subMenuItems: SubMenuItem[]; onClick?: never }
  | { subMenuItems?: never; onClick?: () => void };

export type NestedDropdownMenuType = {
  label: string;
  icon?: React.ReactNode;
} & ExclusiveSubMenuItem;

export interface NestedDropdownMenuProps {
  trigger?: React.ReactNode;
  menuItems: NestedDropdownMenuType[];
  side?: 'left' | 'right';
  onOpenChange?: (open: boolean) => void;
  onDismiss?: () => void;
  'data-testid'?: string;
}

export const NestedDropdownMenu = ({
  trigger = (
    <Button
      size="small"
      variant="ghost"
      leftIcon="radix-icons:dots-horizontal"
    />
  ),
  menuItems,
  side = 'left',
  onOpenChange,
  onDismiss,
  'data-testid': dataTestId = 'nested-dropdown-menu',
}: NestedDropdownMenuProps) => {
  const handleOpenChange = (open: boolean) => {
    if (!open && onDismiss) {
      onDismiss();
    }
    if (onOpenChange) {
      onOpenChange(open);
    }
  };

  return (
    <DropdownMenu onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger asChild data-testid={dataTestId}>
        {trigger}
      </DropdownMenuTrigger>
      <DropdownMenuContent side={side} align="start">
        {menuItems.map((item, index) => (
          <Fragment key={index}>
            {item.onClick && (
              <DropdownMenuItem
                onClick={item.onClick}
                data-testid={`${dataTestId}-${index}`}
              >
                {item.icon} {item.label}
              </DropdownMenuItem>
            )}
            {item.subMenuItems && item.subMenuItems.length > 0 && (
              <DropdownMenuSub>
                <DropdownMenuSubTrigger
                  data-testid={`${dataTestId}-sub-menu-trigger-${index}`}
                >
                  {item.label}
                </DropdownMenuSubTrigger>
                <DropdownMenuPortal>
                  <DropdownMenuSubContent className="bg-white dark:bg-[#11171C]">
                    {item.subMenuItems.map((subItem, subIndex) => (
                      <DropdownMenuItem
                        key={subIndex}
                        onClick={subItem.onClick}
                        data-testid={`${dataTestId}-sub-menu-item-${index}-${subIndex}`}
                      >
                        {subItem.icon} {subItem.label}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuSubContent>
                </DropdownMenuPortal>
              </DropdownMenuSub>
            )}
          </Fragment>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
